import { GUARD_PROBE_PROFILES, type GuardProbeProfile } from '@nadoutong/contracts';
import { useT } from '../../../i18n/index.ts';
import { Chip, Input, Switch } from '../../../ui/index.ts';
import { Segment } from './GuardControls.tsx';
import { profileMissing, type GuardIssue, type ProfileDraft, type SkuDraft } from './guardModel.ts';
import { GUARD_SIGNAL_VIEWS, profileLabel } from './guardSignals.ts';

const PREFIX = 'guaranteed-guard-profile';

function Cell({
  value,
  label,
  issue,
  disabled,
  testId,
  onChange,
}: {
  readonly value: string;
  readonly label: string;
  readonly issue: GuardIssue | undefined;
  readonly disabled: boolean;
  readonly testId: string;
  readonly onChange: (text: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <span className="inline-flex flex-col gap-0.5">
      <Input
        compact
        mono
        wrapperClassName="w-16"
        className="text-right"
        value={value}
        disabled={disabled}
        inputMode="numeric"
        invalid={issue !== undefined}
        aria-label={label}
        data-testid={testId}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      {issue === undefined ? null : (
        <span role="alert" className="text-micro text-bad" data-testid={`${testId}-error`}>{t(issue.key, issue.params)}</span>
      )}
    </span>
  );
}

function ProfileRow({
  profile,
  draft,
  issues,
  disabled,
  onChange,
}: {
  readonly profile: GuardProbeProfile;
  readonly draft: ProfileDraft;
  readonly issues: ReadonlyMap<string, GuardIssue>;
  readonly disabled: boolean;
  readonly onChange: (next: ProfileDraft) => void;
}): JSX.Element {
  const t = useT();
  const off = disabled || !draft.enabled;
  const cell = (key: 'interval' | 'shots' | 'timeout' | 'minStream' | 'maxGap', label: Parameters<typeof t>[0]): JSX.Element => (
    <Cell
      value={draft[key]}
      label={t(label)}
      issue={issues.get(`profile.${profile}.${key}`)}
      disabled={off}
      testId={`${PREFIX}-${profile}-${key}`}
      onChange={(text) => onChange({ ...draft, [key]: text })}
    />
  );
  return (
    <tr className="border-b border-line-soft align-middle" data-testid={`${PREFIX}-row-${profile}`}>
      <td className="px-2.5 py-2">
        <b className="font-medium text-paper">{t(profileLabel(profile))}</b>
      </td>
      <td className="px-2.5 py-2">
        <Switch
          checked={draft.enabled}
          disabled={disabled}
          label={t('guaranteed.guard.recover.col.enabled')}
          testId={`${PREFIX}-${profile}-enabled`}
          onCheckedChange={(enabled) => onChange({ ...draft, enabled })}
        />
      </td>
      <td className="px-2.5 py-2 text-label text-paper-mid">
        {profile === 'short' ? t('guaranteed.guard.recover.short_criteria') : (
          <span className="flex flex-wrap items-center gap-1.5">
            {t('guaranteed.guard.recover.long_min_stream')}
            {cell('minStream', 'guaranteed.guard.recover.long_min_stream')}
            {t('guaranteed.guard.unit.seconds')}
            · {t('guaranteed.guard.recover.long_max_gap')}
            {cell('maxGap', 'guaranteed.guard.recover.long_max_gap')}
            {t('guaranteed.guard.unit.seconds')}
            · {t('guaranteed.guard.recover.long_and_short')}
          </span>
        )}
      </td>
      <td className="px-2.5 py-2">{cell('interval', 'guaranteed.guard.recover.col.interval')}</td>
      <td className="px-2.5 py-2">{cell('shots', 'guaranteed.guard.recover.col.shots')}</td>
      <td className="px-2.5 py-2">{cell('timeout', 'guaranteed.guard.recover.col.timeout')}</td>
    </tr>
  );
}

export function GuardRecoverTables({
  draft,
  issues,
  disabled,
  onChange,
}: {
  readonly draft: SkuDraft;
  readonly issues: ReadonlyMap<string, GuardIssue>;
  readonly disabled: boolean;
  readonly onChange: (next: SkuDraft) => void;
}): JSX.Element {
  const t = useT();
  const th = 'px-2.5 py-1.5 text-left text-label font-normal text-paper-low';
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-line bg-card p-4" data-testid="guaranteed-guard-recover">
      <h3 className="m-0 text-section text-paper">{t('guaranteed.guard.recover.title')}</h3>
      <div>
        <h4 className="m-0 text-dense text-paper">{t('guaranteed.guard.recover.profiles_title')}</h4>
        <p className="m-0 mt-1 text-label text-paper-mid">{t('guaranteed.guard.recover.profiles_desc')}</p>
      </div>
      <table className="w-full border-collapse text-dense">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>{t('guaranteed.guard.recover.col.profile')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.enabled')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.success')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.interval')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.shots')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.timeout')}</th>
          </tr>
        </thead>
        <tbody>
          {GUARD_PROBE_PROFILES.map((profile) => (
            <ProfileRow
              key={profile}
              profile={profile}
              draft={draft.profiles[profile]}
              issues={issues}
              disabled={disabled}
              onChange={(next) => onChange({ ...draft, profiles: { ...draft.profiles, [profile]: next } })}
            />
          ))}
        </tbody>
      </table>
      <div>
        <h4 className="m-0 text-dense text-paper">{t('guaranteed.guard.recover.lock_title')}</h4>
        <p className="m-0 mt-1 text-label text-paper-mid">{t('guaranteed.guard.recover.lock_desc')}</p>
      </div>
      <table className="w-full border-collapse text-dense">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>{t('guaranteed.guard.recover.col.fault')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.lock')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.clean')}</th>
            <th className={th}>{t('guaranteed.guard.recover.col.hold')}</th>
          </tr>
        </thead>
        <tbody>
          {GUARD_SIGNAL_VIEWS.map((view) => {
            const kind = view.spec.kind;
            const signal = draft.signals[kind];
            const setSignal = (patch: Partial<typeof signal>): void => {
              onChange({ ...draft, signals: { ...draft.signals, [kind]: { ...signal, ...patch } } });
            };
            return (
              <tr key={kind} className="border-b border-line-soft align-middle" data-testid={`guaranteed-guard-lock-${kind}`}>
                <td className="px-2.5 py-2 text-paper">{t(view.title)}</td>
                <td className="px-2.5 py-2">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <Segment
                      value={signal.recoverProfile}
                      disabled={disabled}
                      ariaLabel={t('guaranteed.guard.recover.col.lock')}
                      testId={`guaranteed-guard-lock-${kind}-profile`}
                      onChange={(recoverProfile) => setSignal({ recoverProfile })}
                      options={GUARD_PROBE_PROFILES.map((profile) => ({
                        value: profile,
                        label: t(profileLabel(profile)),
                        tone: 'bg-signal-dim text-signal',
                      }))}
                    />
                    {view.recommendLong && signal.recoverProfile === 'short' ? (
                      <Chip tone="signal" lamp={false}>{t('guaranteed.guard.recover.recommend_long')}</Chip>
                    ) : null}
                  </span>
                  {profileMissing(draft, kind) ? (
                    <p role="alert" className="m-0 mt-1 text-micro text-warn" data-testid={`guaranteed-guard-lock-${kind}-missing`}>
                      {t('guaranteed.guard.recover.profile_missing')}
                    </p>
                  ) : null}
                </td>
                <td className="px-2.5 py-2">
                  <span className="flex items-center gap-1.5">
                    <Cell
                      value={signal.clean}
                      label={t('guaranteed.guard.recover.col.clean')}
                      issue={issues.get(`${kind}.clean`)}
                      disabled={disabled}
                      testId={`guaranteed-guard-lock-${kind}-clean`}
                      onChange={(clean) => setSignal({ clean })}
                    />
                    <span className="text-micro text-paper-low">{t('guaranteed.guard.unit.times')}</span>
                  </span>
                </td>
                <td className="px-2.5 py-2 font-mono text-paper-mid">
                  {`${signal.trip.hold} ${t('guaranteed.guard.unit.seconds')}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
